import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRemoveCoupon } from "../../hooks/useRemoveCoupon";
import { useCouponLabel } from "../../hooks/useCouponLabel";
import type { TransformedCart } from "../../types/cartApiTypes";
import Icon from "../../../../../common/components/Icon/Icon";
import { useAppDispatch } from "../../../../../common/hooks/hooks";
import { openGlobalModal } from "../../../../../common/features/globalModal/globalModalSlice";

interface CartTotalsProps {
  cart: TransformedCart;
  showViewCartButton?: boolean;
  context?: "page" | "panel";
}

/**
 * Reusable component for displaying cart totals, coupons, and checkout buttons.
 * Used in both the Cart Page and the Cart Offcanvas Panel.
 */
const CartTotals: React.FC<CartTotalsProps> = ({
  cart,
  showViewCartButton = false,
  context = "page",
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { totals, coupons } = cart;
  const [removingCode, setRemovingCode] = useState<string | null>(null);

  // --- State for collapsible functionality in 'panel' context ---
  const [isCollapsed, setIsCollapsed] = useState(context === "panel");

  const { handleRemoveCoupon: triggerRemoveCoupon, isRemoving } =
    useRemoveCoupon();
  const { getCouponLabel } = useCouponLabel();

  // Effect to reset the specific coupon's loading state when the mutation is complete.
  useEffect(() => {
    if (!isRemoving) {
      setRemovingCode(null);
    }
  }, [isRemoving]);

  const handleRemoveCoupon = (code: string) => {
    if (isRemoving) return;
    setRemovingCode(code);
    triggerRemoveCoupon({ code });
  };

  const handleToggleCollapse = () => {
    if (context === "panel") {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleOpenCouponModal = () => {
    dispatch(
      openGlobalModal({
        contentKey: "applyCoupon",
        title: t("cartOffcanvas.applyCouponTitle", "Apply Coupon"),
        contentProps: { isModalContext: true },
      }),
    );
  };

  const handleOpenShippingModal = () => {
    dispatch(
      openGlobalModal({
        contentKey: "shippingCalculator",
        title: t("cartPage.shippingCalculator.title", "Shipping Options"),
        contentProps: { namePrefix: "panel", isModalContext: true },
      }),
    );
  };

  const rootClass = `${`cart-totals cart-totals--${context}`} ${isCollapsed ? "is-collapsed" : ""}`;

  return (
    <div className={rootClass}>
      {/* Only show title on the full page, not in the panel footer */}
      {context === "page" && (
        <h2 className="cart-totals__title">
          {t("cartTotals.title", "Cart Totals")}
        </h2>
      )}

      <div className="cart-totals__collapsible-content">
        {/* Applied Coupons */}
        {coupons && coupons.length > 0 && (
          <div className="applied-coupons">
            <ul className="applied-coupons__list">
              {coupons.map((coupon) => {
                const isThisOneRemoving =
                  isRemoving && removingCode === coupon.code;
                return (
                  <li key={coupon.code} className="applied-coupons__item">
                    <span className="applied-coupons__code">
                      <Icon iconName="ticket-outlined" />{" "}
                      {getCouponLabel(coupon.code)}
                    </span>
                    <span className="applied-coupons__discount">
                      -{coupon.totals.displayDiscount}
                    </span>
                    {coupon.isRemovable && (
                      <button
                        className="applied-coupons__remove"
                        onClick={() => handleRemoveCoupon(coupon.code)}
                        disabled={isThisOneRemoving}
                        aria-label={t("cartTotals.appliedCoupons.removeLabel", {
                          code: coupon.code,
                        })}
                      >
                        {isThisOneRemoving ? (
                          <div className="loader"></div>
                        ) : (
                          <span>&times;</span>
                        )}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* --- DYNAMIC SUBTLE TAX BREAKDOWN --- */}
        {/* Rendered via custom CSS class instead of inline styles */}
        {totals.hasTax && (
          <div className="cart-totals__tax-breakdown">
            {t("cartTotals.taxBreakdown", {
              net: totals.totalItemsNet,
              tax: totals.totalItemsTax,
              defaultValue: `Net ${totals.totalItemsNet} + Vergiler ${totals.totalItemsTax}`,
            })}
          </div>
        )}

        {/* 1. Item Subtotal (Ara Toplam) */}
        <div className="cart-totals__row">
          <span>{t("cartTotals.subtotal", "Subtotal")}</span>
          <span>{totals.totalItems}</span>
        </div>

        {/* --- DELETED: Standard separate "Vergi(ler)" row has been removed from here --- */}

        {/* 3. Shipping (Gönderim Ücreti) */}
        {/* Conditional Render: Only show if cart needs shipping and shipping has been calculated */}
        {cart.needsShipping && cart.hasCalculatedShipping && (
          <div className="cart-totals__row">
            <span>{t("cartTotals.shipping", "Shipping")}</span>
            <span
              className={totals.shippingStatus === "free" ? "is-success" : ""}
            >
              {
                {
                  free: t("cartTotals.freeShipping", "Free Shipping!"),
                  pickup: t("cartTotals.localPickup", "Local Pickup"),
                  paid: totals.totalShipping,
                  none: t(
                    "cartTotals.noShippingOptions",
                    "No shipping options available.",
                  ),
                }[totals.shippingStatus]
              }
            </span>
          </div>
        )}

        {/* 4. Fees (Optional - usually goes with shipping) */}
        {totals.hasFees && (
          <div className="cart-totals__row">
            <span>{t("cartTotals.fees", "Fees")}</span>
            <span>{totals.totalFees}</span>
          </div>
        )}

        {/* 5. Discount (İndirimler) */}
        {totals.hasDiscount && (
          <div className="cart-totals__row">
            <span>{t("cartTotals.discount", "Discount")}</span>
            <span className="is-discount">-{totals.totalDiscount}</span>
          </div>
        )}
      </div>

      {context === "panel" && (
        <div className="cart-totals__details-actions">
          <button
            className="cart-totals__action-btn"
            onClick={handleOpenCouponModal}
          >
            {t("cartOffcanvas.useCoupon", "Use Coupon")}
          </button>
          {cart.needsShipping && (
            <button
              className="cart-totals__action-btn"
              onClick={handleOpenShippingModal}
            >
              {t("cartOffcanvas.calculateShipping", "Calculate Shipping")}
            </button>
          )}
          <button
            className="cart-totals__toggle"
            onClick={handleToggleCollapse}
            aria-expanded={!isCollapsed}
          >
            <span>
              {isCollapsed
                ? t("cartOffcanvas.showDetails", "Show Details")
                : t("cartOffcanvas.hideDetails", "Hide Details")}
            </span>
            <Icon iconName="chevron-down" />
          </button>
        </div>
      )}

      {/* Grand Total */}
      <div className="cart-totals__total">
        <span>{t("cartTotals.total", "Total")}</span>
        <span>{totals.totalPrice}</span>
      </div>

      <div className="cart-totals__actions">
        {showViewCartButton && (
          <a
            href={window.commonVariables?.cartUrl || "/cart"}
            className="cart-totals__btn cart-totals__btn--secondary"
          >
            <Icon
              iconName="shopping-cart-outline"
              className="icon--stroke-thick"
            />
            <span>{t("cartOffcanvas.viewCart", "View Cart")}</span>
          </a>
        )}
        <a
          href={window.commonVariables?.checkoutUrl || "/checkout"}
          className="cart-totals__btn cart-totals__btn--primary"
        >
          <Icon iconName="credit-card-security" className="icon--filled" />
          <span>{t("cartTotals.checkout", "Proceed to Checkout")}</span>
        </a>
      </div>
    </div>
  );
};

export default CartTotals;
